import { PauseIcon, PlayIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useWaveformPlayer } from '@/hooks/useWaveformPlayer';
import { formatSecondsToMmSs } from '@/util/dateUtils';

/**
 * Audio preview component for voice samples
 *
 * Features click-to-seek and drag-to-scrub on the waveform for easy navigation.
 *
 * @param previewUrl - URL of the audio preview (.mp3)
 */
export function VoicePreview({ previewUrl }: { previewUrl: string }) {
  const { containerRef, isReady, isPlaying, currentTime, duration, isLoading, playPause } =
    useWaveformPlayer({ url: previewUrl, useAuth: false, barWidth: 2, barGap: 4, height: 24 });

  if (!previewUrl) {
    return null;
  }

  return (
    <div className="mb-4 space-y-2">
      <div className="flex items-center gap-3 rounded-md border p-3">
        <div className="relative min-w-0 flex-1">
          <div ref={containerRef} className="h-6 w-full cursor-pointer" />
          {!isReady && <Skeleton className="absolute inset-0 h-6 w-full" aria-busy={isLoading} />}
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatSecondsToMmSs(duration - currentTime)}
        </span>
        <Button
          type="button"
          size="icon-sm"
          className="shrink-0"
          onClick={playPause}
          disabled={!isReady}
        >
          {isPlaying ? <PauseIcon size={12} /> : <PlayIcon size={12} />}
        </Button>
      </div>
    </div>
  );
}
